import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("hb_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function errMsg(e, fallback = "Something went wrong. Please try again.") {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (d?.message) return d.message;
  if (d?.msg) return d.msg;
  return String(d);
}

export function errDetail(e) {
  return e?.response?.data?.detail;
}

export const api = {
  // auth
  register: (b) => client.post("/auth/register", b),
  login: (b) => client.post("/auth/login", b),
  me: () => client.get("/auth/me"),
  joinSchool: (school_id) => client.post("/auth/join-school", { school_id }),
  // catalog
  products: (params) => client.get("/products", { params }),
  productFacets: (params) => client.get("/products/facets", { params }),
  globalPriceFacets: () => client.get("/product-facets"),
  lockedLook: (product_id, school) => client.get(`/public/locked-look/${product_id}`, { params: { school } }),
  product: (id) => client.get(`/products/${id}`),
  productBySlug: (category, slug) => client.get(`/silo/${category}/${slug}`),
  siloCategory: (category) => client.get(`/silo/${category}`),
  brandHub: (brand, params) => client.get(`/brand/${brand}`, { params }),
  brands: (category) => client.get(`/brands`, { params: category ? { category_slug: category } : {} }),
  homepageStats: () => client.get("/stats/homepage"),
  collections: (occasion) => client.get("/collections", { params: { occasion } }),
  schools: (params) => client.get("/schools", { params }),
  schoolCounties: () => client.get("/school-counties"),
  geoCounties: () => client.get("/geo/counties"),
  geoCities: () => client.get("/geo/cities"),
  geoCounty: (slug) => client.get(`/geo/county/${slug}`),
  geoCity: (slug) => client.get(`/geo/city/${slug}`),
  school: (id) => client.get(`/schools/${id}`),
  schoolEnrichment: (id) => client.get(`/schools/${id}/enrichment`),
  superlatives: (id) => client.get(`/schools/${id}/superlatives`),
  favorites: () => client.get("/favorites"),
  toggleFavorite: (product_id) => client.post("/favorites/toggle", { product_id }),
  // locks
  createLock: (product_id, replace = false, color = null) => client.post("/locks", { product_id, replace, color }),
  myLocks: () => client.get("/locks/mine"),
  productLock: (id, color = null) => client.get(`/products/${id}/lock`, { params: color ? { color } : {} }),
  releaseLock: (id) => client.delete(`/locks/${id}`),
  // cart / orders
  cart: () => client.get("/cart"),
  addCart: (b) => client.post("/cart/add", b),
  removeCart: (product_id) => client.post("/cart/remove", { product_id }),
  orders: () => client.get("/orders"),
  checkout: (b) => client.post("/checkout/session", b),
  checkoutEmbedded: (b) => client.post("/checkout/embedded", b),
  checkoutIntent: (b) => client.post("/checkout/intent", b),
  intentStatus: (id) => client.get(`/checkout/intent/${id}/status`),
  orderReceipt: (id) => client.get(`/orders/${id}/receipt`),
  checkoutStatus: (sid) => client.get(`/checkout/status/${sid}`),
  // ai
  fit: (b) => client.post("/ai/fit", b),
  upload: (formData) => client.post("/ai/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  tryOn: (b) => client.post("/ai/tryon", b),
  tryOnLatest: (product_id) => client.get("/ai/tryon/latest", { params: { product_id } }),
  tryOnMine: () => client.get("/ai/tryon/mine"),
  tryOnCredits: () => client.get("/ai/tryon/credits"),
  tryonHeroImages: () => client.get("/tryon/hero-images"),
  productsCount: (params) => client.get("/products/count", { params }),
  tryOnTiers: () => client.get("/tryon/tiers"),
  caption: (vibe) => client.post("/memories/caption", { vibe }),
  // social
  squads: () => client.get("/squads/mine"),
  createSquad: (b) => client.post("/squads", b),
  squad: (id) => client.get(`/squads/${id}`),
  addBoard: (id, b) => client.post(`/squads/${id}/board`, b),
  memories: (params) => client.get("/memories", { params }),
  createMemory: (b) => client.post("/memories", b),
  reactMemory: (id, type) => client.post(`/memories/${id}/react`, { type }),
  viewMemory: (id, viewer) => client.post(`/memories/${id}/view`, { viewer }),
  deleteMemory: (id) => client.delete(`/memories/${id}`),
  productLooks: (id) => client.get(`/products/${id}/looks`),
  mostLoved: (schoolId) => client.get(`/schools/${schoolId}/most-loved`),
  lookbookCommunity: () => client.get("/lookbook/community"),
  promCupLeaderboard: (limit = 10) => client.get("/prom-cup/leaderboard", { params: { limit } }),
  recentLocks: (limit = 20) => client.get("/locks/recent", { params: { limit } }),
  trendingBySchool: (school_id) => client.get("/trending-by-school", { params: school_id ? { school_id } : {} }),
  schoolLocksWeek: (school_id) => client.get("/school-locks-week", { params: school_id ? { school_id } : {} }),
  silo: (category_slug) => client.get(`/silo/${category_slug}`),
  boosters: (school_id) => client.get("/boosters", { params: { school_id } }),
  donate: (b) => client.post("/boosters/donate", b),
  // admin
  adminStats: () => client.get("/admin/stats"),
  adminProducts: () => client.get("/admin/products"),
  createProduct: (b) => client.post("/admin/products", b),
  importProducts: (formData) => client.post("/admin/products/import", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  deleteProduct: (id) => client.delete(`/admin/products/${id}`),
  adminLocks: () => client.get("/admin/locks"),
  overrideLock: (id) => client.post(`/admin/locks/${id}/override`),
  moderation: () => client.get("/admin/moderation"),
  moderate: (id, approve) => client.post(`/admin/moderation/${id}`, { approve }),
  adminBrands: () => client.get("/admin/brands"),
  updateBrand: (slug, body) => client.put(`/admin/brands/${slug}`, body),
  adminOrders: () => client.get("/admin/orders"),
  adminDonations: () => client.get("/admin/donations"),
  adminTryons: () => client.get("/admin/tryons"),
  featureTryon: (id, featured) => client.post(`/admin/tryons/${id}/feature`, { featured }),
  adminBookings: () => client.get("/admin/bookings"),
  adminStylistBookings: () => client.get("/admin/stylist-bookings"),
  resetLookbook: () => client.post("/admin/lookbook/reset"),
  resetPromCup: () => client.post("/admin/prom-cup/reset"),
  adminVipSubscribers: (limit = 500) => client.get(`/admin/vip-subscribers?limit=${limit}`),
  vipSubscribe: (body) => client.post("/vip/subscribe", body),
  // Family / parent-guardian
  familyInvite: (body) => client.post("/family/invite", body),
  familyInvitePreview: (token) => client.get(`/family/invite/${token}`),
  familyAccept: (body) => client.post("/family/accept", body),
  familyMine: () => client.get("/family/mine"),
  familyRemove: (linkId) => client.delete(`/family/link/${linkId}`),
  familySetActiveChild: (studentId) => client.post("/family/active-child", { student_id: studentId }),
  familyChildLookbook: (studentId) => client.get(`/family/child/${studentId}/lookbook`),
  // Session management
  logoutAll: () => client.post("/auth/logout-all"),
  // Admin: email previews
  adminEmailPreviews: () => client.get("/admin/email-previews"),
  createStylistBooking: (b) => client.post("/bookings/stylist", b),
  // School magic-link verification
  requestSchoolVerify: (b) => client.post("/auth/school/request-verify", b),
  resendSchoolVerify: (b) => client.post("/auth/school/resend", b),
  confirmSchoolVerify: (token) => client.post("/auth/school/verify", { token }),
  requestAddSchool: (b) => client.post("/auth/school/request-add", b),
  // Support tickets
  supportTicket: (b) => client.post("/support/ticket", b),
  adminSupportTickets: (limit = 100) => client.get(`/admin/support/tickets?limit=${limit}`),
  // Contact inquiries (general questions, appointments, press — routes to info@)
  contactInquiry: (b) => client.post("/contact/inquiry", b),
  adminContactInquiries: (limit = 100) => client.get(`/admin/contact/inquiries?limit=${limit}`),
  // Product reviews
  productReviews: (productId, limit = 20, offset = 0) => client.get(`/products/${productId}/reviews?limit=${limit}&offset=${offset}`),
  createProductReview: (productId, b) => client.post(`/products/${productId}/reviews`, b),
  markReviewHelpful: (reviewId) => client.post(`/reviews/${reviewId}/helpful`),
};

export default client;
